import React, { useState, useEffect, useRef } from 'react';
import { Row, Col } from 'antd';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { LinkedinOutlined, GithubOutlined } from '@ant-design/icons';
import { CONTACT_PHONE } from '../config/servicesConfig';
import { footerHeight } from '../utils/footerHeight';
import { SERVICE_AREA_LINE } from '../config/serviceArea';


const FooterContainer = styled.footer`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100vw;
  background: var(--glass-bg);
  backdrop-filter: blur(var(--glass-blur, 35px)) saturate(var(--glass-saturation, 300%));
  -webkit-backdrop-filter: blur(var(--glass-blur, 35px)) saturate(var(--glass-saturation, 300%));
  border: 1px solid var(--glass-border);
  border-bottom: none;
  border-left: none;
  border-right: none;
  box-shadow: 
    0 -20px 60px rgba(0, 0, 0, 0.06),
    0 -8px 25px rgba(0, 0, 0, 0.04),
    0 -2px 8px rgba(0, 0, 0, 0.02),
    inset 0 2px 0 rgba(255, 255, 255, 0.3),
    inset 0 -2px 0 rgba(0, 0, 0, 0.03);
  border-radius: 28px 28px 0 0;
  overflow: hidden;
  will-change: transform;
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(100%)'};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 1.5rem 0 1rem 0;
  z-index: 1000;
  
  /* Enhanced glass-like depth effect with dark variant */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.15) 0%,
      rgba(255, 255, 255, 0.05) 25%,
      rgba(255, 255, 255, 0.02) 50%,
      rgba(0, 0, 0, 0.01) 75%,
      rgba(0, 0, 0, 0.03) 100%
    );
    border-radius: inherit;
    pointer-events: none;
    z-index: 1;
  }
  
  /* Enhanced glass reflection effect with transparency */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 40%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.12) 0%,
      rgba(255, 255, 255, 0.04) 50%,
      transparent 100%
    );
    border-radius: 28px 28px 0 0;
    pointer-events: none;
    z-index: 2;
  }
  
  /* Content positioning */
  > * {
    position: relative;
    z-index: 3;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

// DWEB-213: a plain span, not antd Typography's Text (see About.js). What Text supplied
// is restated from antd 5.27.4 so nothing visible moves: 14px with the token line-height
// UNITLESS (the social icons are 24px and inherited the ratio, 37.7px), word-break, and
// Typography's link rules (operationUnit + getLinkStyles). Those rules made every link in
// the footer antd's link blue, overriding the site's own FooterLink / FooterAnchor /
// SocialIcon colours; they are matched here DELIBERATELY for this bundle release, and
// whether the site's declared colours should win instead is a separate design decision.
// '& a' sits at the specificity antd's '.ant-typography a' had, so it wins the same way.
// The hover/focus rule also carries text-decoration: none — Typography's hover rule suppressed the
// site's own FooterLink / FooterAnchor hover underline, and without it the phone, Applications and
// Privacy Policy links gained an underline on hover (found by the forced-state capture; matched
// deliberately, like the link color, and held by typography-eager-path.spec.js). Comments stay OUT of
// the template literal: a // line inside it is template text, ships in the bundle, and is read as copy.
const FooterText = styled.span.attrs({ 'data-typo': '' })`
  font-size: 14px;
  line-height: 1.5714285714285714;
  word-break: break-word;

  & a {
    color: #1677ff;
    text-decoration: none;
    outline: none;
    cursor: pointer;
    transition: all 0.3s;
    border: 0;
    padding: 0;
    background: none;
    user-select: text;
  }

  & a:focus-visible {
    outline: 3px solid #91caff;
    outline-offset: 1px;
    transition: outline-offset 0s, outline 0s;
  }

  & a:hover,
  & a:focus {
    color: #69b1ff;
    text-decoration: none;
  }

  & a:active {
    color: #0958d9;
  }
  color: rgba(255, 255, 255, 0.95);
  text-align: center;
  display: block;
  margin-bottom: 4px;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.8);
`;

const FooterLink = styled(Link)`
  color: #60A5FA;
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover {
    color: #FFF;
    text-decoration: underline;
  }
`;

/** DWEB-140: the phone line, a normal business route beside the address — the same tel: as the receptionist card, no copy beyond the number. */
const FooterAnchor = styled.a`
  color: #60A5FA;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #FFF;
    text-decoration: underline;
  }
`;

const SocialIcon = styled.a`
  color: rgba(255, 255, 255, 0.95);
  font-size: 24px;
  transition: color 0.3s ease;
  margin: 0 8px;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.8);
  
  &:hover {
    color: #60A5FA;
  }
`;

/** Footer control that reopens the analytics choice (DWEB-15). */
const AnalyticsPreferencesButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-decoration: underline;
  opacity: 0.85;

  &:hover {
    opacity: 1;
  }
`;

function Footer() {
  const [isVisible, setIsVisible] = useState(false);
  const footerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = document.documentElement.scrollTop;
      const clientHeight = document.documentElement.clientHeight;
      
      // Calculate remaining content height below current viewport
      const remainingContentHeight = scrollHeight - (scrollTop + clientHeight);
      
      // DWEB-204: rise on the footer's OWN height — the same declared value
      // #root reserves as padding-bottom. The footer then comes up as the
      // empty band enters the viewport and, at maximum scroll, fills exactly
      // that band, over no content. (It was min(30% of the viewport, 300px),
      // which rose over content still mid-section.)
      const isNearBottom = remainingContentHeight <= footerHeight();
      
      setIsVisible(isNearBottom);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll); // Recalculate on window resize

    // DWEB-206: the reserve is MEASURED, not declared. This footer's height
    // depends on how the service-area sentence wraps (one, two or three lines)
    // and on fonts, so any declared number is too much somewhere. Observe this
    // box and write its height, rounded UP, to --footer-height on :root — the
    // one property #root's padding and footerHeight() already read. index.css
    // keeps a first-paint estimate for the moment before this runs. The reveal
    // is re-checked after each write, so it rises on the new value at once.
    const root = document.documentElement;
    const el = footerRef.current;
    let observer = null;
    if (el && typeof ResizeObserver !== 'undefined') {
      const writeReserve = () => {
        const px = Math.ceil(el.getBoundingClientRect().height);
        if (px > 0) root.style.setProperty('--footer-height', `${px}px`);
        handleScroll();
      };
      observer = new ResizeObserver(writeReserve);
      observer.observe(el);
      writeReserve();
    }

    // Check initial position
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (observer) {
        observer.disconnect();
        root.style.removeProperty('--footer-height');
      }
    };
  }, []);

  return (
    <FooterContainer ref={footerRef} isVisible={isVisible}>
      <FooterContent>
        <Row justify="center" align="middle">
          <Col xs={24}>
            <FooterText>
              Copyright © {new Date().getFullYear()} DynamWorks LLC | All Rights Reserved
            </FooterText>
            <FooterText>
              10 W Bay State St #7004, Alhambra, CA 91802
            </FooterText>
            <FooterText>
              {/* DWEB-206: the service area, as a sentence under the address.
                  Owner-approved copy with one home; the prerender writes the
                  same export into the static block, because this footer is not
                  in the layer a non-rendering crawler receives. */}
              {SERVICE_AREA_LINE}
            </FooterText>
            <FooterText>
              <FooterAnchor href={CONTACT_PHONE.href} aria-label={CONTACT_PHONE.label}>
                {CONTACT_PHONE.display}
              </FooterAnchor>
            </FooterText>
            <FooterText>
              {/* DWEB-190: the one link to /work in the RENDERED app. The
                  prerendered nav that also links it is discarded when React
                  mounts, so without this a rendering crawler reaches /work
                  through the sitemap alone. */}
              <FooterLink to="/work">Applications</FooterLink>
            </FooterText>
            <FooterText>
              <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
            </FooterText>
            <FooterText>
              {/* DWEB-15: the analytics choice has to be revisitable, not a
                  one-time gate. ConsentBanner publishes the reopener; if it has
                  unmounted this is a no-op rather than an error. */}
              <AnalyticsPreferencesButton
                type="button"
                onClick={() => window.__openAnalyticsPreferences?.()}
              >
                Analytics preferences
              </AnalyticsPreferencesButton>
            </FooterText>
            <FooterText style={{ marginTop: '8px' }}>
              <SocialIcon 
                href="https://www.linkedin.com/company/dynamworks" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <LinkedinOutlined />
              </SocialIcon>
              <SocialIcon 
                href="https://github.com/dynamworks" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <GithubOutlined />
              </SocialIcon>
            </FooterText>
          </Col>
        </Row>
      </FooterContent>
    </FooterContainer>
  );
}

export default Footer;
